import {
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { droitImageEntryType } from '../../../../lib/api/api';
import { useAlert } from '../../../../lib/alert';
import { IconButton } from '../../../../components/IconButton';
import { pathHandler } from '../../../../lib/pathHandler';
import { convertToFrenchDate } from '../../../../lib/utils';

type orderByColumnType = 'created_at' | 'firstName' | 'lastName';

type orderByType = { column: orderByColumnType; direction: 'asc' | 'desc' };

function DroitImageTable(props: { droitImageEntries: droitImageEntryType[] }) {
    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState<orderByType | undefined>({
        column: 'created_at',
        direction: 'desc',
    });
    const { displayAlert } = useAlert();

    const sortedDroitImageEntries = sortData(props.droitImageEntries);

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'created_at'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('created_at')}
                        >
                            Date de réception
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'firstName'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('firstName')}
                        >
                            Prénom
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'lastName'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('lastName')}
                        >
                            NOM
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>Décision</TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {sortedDroitImageEntries.map((droitImageEntry) => {
                    const rowData = computeRowData(droitImageEntry);
                    return (
                        <TableRow key={droitImageEntry.id}>
                            {rowData.map((cellData, index) => (
                                <TableCell key={index}>{cellData}</TableCell>
                            ))}
                            <TableCell>
                                <CellRow>
                                    <IconButton
                                        color="primary"
                                        size="small"
                                        title="Copier la ligne"
                                        IconComponent={ContentCopyIcon}
                                        onClick={() => copyRowToClipboard(rowData)}
                                    />
                                    <IconButton
                                        color="primary"
                                        size="small"
                                        title="Aller au formulaire"
                                        IconComponent={ArrowForwardIcon}
                                        onClick={() => navigateToEntryPreview(droitImageEntry.id)}
                                    />
                                </CellRow>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    function sortData(data: droitImageEntryType[]) {
        if (orderBy === undefined) {
            return data;
        }
        return data.sort((a, b) => {
            const aValue = a[orderBy.column];
            const bValue = b[orderBy.column];
            if (aValue === bValue) {
                return 0;
            }
            const comparison = aValue.localeCompare(bValue);

            return orderBy.direction === 'asc' ? comparison : -comparison;
        });
    }

    function buildSortHandler(orderByColumn: orderByColumnType) {
        return () => {
            if (orderBy === undefined || orderBy.column !== orderByColumn) {
                setOrderBy({ column: orderByColumn, direction: 'asc' });
                return;
            } else {
                switch (orderBy.direction) {
                    case 'asc':
                        setOrderBy({ column: orderByColumn, direction: 'desc' });
                        break;
                    case 'desc':
                        setOrderBy({ column: orderByColumn, direction: 'asc' });
                        break;
                }
            }
        };
    }

    function computeRowData(droitImageEntry: droitImageEntryType): string[] {
        const rowData = [
            convertToFrenchDate(droitImageEntry.created_at.substring(0, 10)),
            droitImageEntry.firstName.toUpperCase(),
            droitImageEntry.lastName.toUpperCase(),
            droitImageEntry.optinStatus,
        ];
        return rowData;
    }

    function navigateToEntryPreview(droitImageId: string) {
        const path = pathHandler.getRoutePath(
            'DROIT_IMAGE_PREVIEW',
            {
                droitImageId,
            },
            { role: 'admin' },
        );
        navigate(path);
    }

    async function copyRowToClipboard(rowData: string[]) {
        const textToCopy = rowData.join('\t'); // Use tab as a separator for
        try {
            await navigator.clipboard.writeText(textToCopy);
            displayAlert({
                variant: 'success',
                text: 'Ligne copiée dans le presse-papiers.',
            });
        } catch (error) {
            displayAlert({
                variant: 'error',
                text: `Échec de la copie dans le presse-papiers: ${error}`,
            });
        }
    }
}

const CellRow = styled('div')(({ theme }) => ({
    display: 'flex',
}));

export { DroitImageTable };
